/**
 * @fileoverview
 * Date Utility Functions
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

const SHORT_MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export class DateUtil {
  /**
   * Format date to dd/MM/yyyy or dd-MM-yyyy (or dd-MM-yy when shortYear is true).
   * When date is an ISO 8601 string with timezone (e.g. 2026-03-12T00:15:30.351+05:30), uses the
   * date part from the string so the API's calendar date is shown regardless of browser timezone.
   * @param date - Date object, date string, or null/undefined (returns '-' when null/invalid)
   * @param separator - Separator character ('/' or '-'), defaults to '/'
   * @param useUtc - When true, use UTC date (for API dates that represent a calendar day in UTC). Ignored when date is ISO with offset.
   * @param shortYear - When true, use 2-digit year (e.g. DD-MM-YY)
   */
  static formatDate(
    date: Date | string | null | undefined,
    separator: '/' | '-' = '/',
    useUtc = false,
    shortYear = false
  ): string {
    if (date == null || date === '') return '-';
    const dateStr = typeof date === 'string' ? date : undefined;
    const isoDateMatch = dateStr ? /^(\d{4})-(\d{2})-(\d{2})T/.exec(dateStr) : null;
    if (isoDateMatch) {
      const [, y, m, d] = isoDateMatch;
      const yearStr = shortYear ? String(y).slice(-2) : y;
      return `${d}${separator}${m}${separator}${yearStr}`;
    }
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '-';
    const day = useUtc ? d.getUTCDate() : d.getDate();
    const month = useUtc ? d.getUTCMonth() + 1 : d.getMonth() + 1;
    const year = useUtc ? d.getUTCFullYear() : d.getFullYear();
    const yearStr = shortYear ? String(year).slice(-2) : String(year);
    return `${String(day).padStart(2, '0')}${separator}${String(month).padStart(2, '0')}${separator}${yearStr}`;
  }

  /**
   * Format date to "d MMM yyyy" (e.g. 11 Feb 2026)
   */
  static formatDateShort(date: Date | string): string {
    const d = new Date(date);
    const day = d.getDate();
    const month = SHORT_MONTH_NAMES[d.getMonth()];
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  }

  /**
   * Format date to dd/MM/yyyy HH:mm:ss
   */
  static formatDateTime(date: Date | string): string {
    const d = new Date(date);
    const dateStr = this.formatDate(d);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${dateStr} ${hours}:${minutes}:${seconds}`;
  }

  /**
   * Format date to HH:mm:ss
   */
  static formatTime(date: Date | string): string {
    const d = new Date(date);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  /**
   * Get relative time (e.g., "2 hours ago")
   */
  static getRelativeTime(date: Date | string): string {
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'Just now';
  }

  /**
   * Check if date is today
   */
  static isToday(date: Date | string): boolean {
    const d = new Date(date);
    const today = new Date();
    return (
      d.getDate() === today.getDate() &&
      d.getMonth() === today.getMonth() &&
      d.getFullYear() === today.getFullYear()
    );
  }

  /**
   * Check if date is valid
   */
  static isValidDate(date: any): boolean {
    return date instanceof Date && !Number.isNaN(date.getTime());
  }

  /**
   * Add days to date
   */
  static addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  /**
   * Subtract days from date
   */
  static subtractDays(date: Date, days: number): Date {
    return this.addDays(date, -days);
  }

  /**
   * Get start of day
   */
  static startOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  /**
   * Get end of day
   */
  static endOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
  }
}

